import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout.js";
import { useNavigate } from "react-router-dom";

const FULFILLMENT_STAGES = [
  { key: "order_placed",      label: "Order Placed" },
  { key: "payment_confirmed", label: "Payment Confirmed" },
  { key: "packaged",          label: "Packaged" },
  { key: "shipped",           label: "Shipped" },
  { key: "out_for_delivery",  label: "Out for Delivery" },
  { key: "delivered",         label: "Delivered" }
];

const STATUS_COLORS = {
  order_placed:      { bg: "#EEF2FF", color: "#4361C2" },
  payment_confirmed: { bg: "#EEF2FF", color: "#4361C2" },
  packaged:          { bg: "#FFF7E6", color: "#B45309" },
  shipped:           { bg: "#E6F0FF", color: "#1D4ED8" },
  out_for_delivery:  { bg: "#FFF0F0", color: "#DC2626" },
  delivered:         { bg: "#EDFDF4", color: "#15803D" }
};

const STATUS_LABELS = {
  order_placed:      "Order Placed",
  payment_confirmed: "Payment Confirmed",
  packaged:          "Packaged",
  shipped:           "Shipped",
  out_for_delivery:  "Out for Delivery",
  delivered:         "Delivered"
};

export default function AdminOrdersScreen() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    fulfillmentStatus: "",
    trackingNumber: "",
    carrier: "",
    note: ""
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await fetch("https://eleven11-i6v4.onrender.com/api/orders");
      const data = await res.json();
      setOrders(data.orders || []);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const openOrder = (order) => {
    setSelected(order);
    setUpdateForm({
      fulfillmentStatus: order.fulfillmentStatus || "order_placed",
      trackingNumber: order.trackingNumber || "",
      carrier: order.carrier || "",
      note: ""
    });
  };

  const pushUpdate = async () => {
    if (!selected) return;
    setUpdating(true);
    try {
      const res = await fetch(`https://eleven11-i6v4.onrender.com/api/orders/${selected._id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateForm)
      });
      const data = await res.json();
      if (data.success) {
        setOrders(prev => prev.map(o => o._id === selected._id ? data.order : o));
        setSelected(data.order);
      }
    } catch (err) {
      console.error("Update failed:", err);
    } finally {
      setUpdating(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesFilter = filter === "all" || order.fulfillmentStatus === filter;
    const matchesSearch = !search ||
      order.orderNumber?.toLowerCase().includes(search.toLowerCase()) ||
      order.customer?.firstName?.toLowerCase().includes(search.toLowerCase()) ||
      order.customer?.lastName?.toLowerCase().includes(search.toLowerCase()) ||
      order.customer?.email?.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <AdminLayout>

      <div style={styles.header}>
        <div>
          <p style={styles.kicker}>ELEVEN CONTROL CENTER</p>
          <h1 style={styles.title}>Orders</h1>
        </div>
        <div style={styles.headerStats}>
          <div style={styles.statChip}>
            <span style={styles.statNum}>{orders.length}</span>
            <span style={styles.statLabel}>Total</span>
          </div>
          <div style={styles.statChip}>
            <span style={{ ...styles.statNum, color: "#f97316" }}>
              {orders.filter(o => o.fulfillmentStatus === "order_placed" || o.fulfillmentStatus === "payment_confirmed").length}
            </span>
            <span style={styles.statLabel}>New</span>
          </div>
          <div style={styles.statChip}>
            <span style={{ ...styles.statNum, color: "#1D4ED8" }}>
              {orders.filter(o => o.fulfillmentStatus === "shipped" || o.fulfillmentStatus === "out_for_delivery").length}
            </span>
            <span style={styles.statLabel}>In Transit</span>
          </div>
          <div style={styles.statChip}>
            <span style={{ ...styles.statNum, color: "#15803D" }}>
              {orders.filter(o => o.fulfillmentStatus === "delivered").length}
            </span>
            <span style={styles.statLabel}>Delivered</span>
          </div>
        </div>
      </div>

      <div style={styles.layout}>

        {/* Orders Table */}
        <div style={styles.tableSection}>

          {/* Toolbar */}
          <div style={styles.toolbar}>
            <input
              style={styles.search}
              placeholder="Search by order #, name, or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div style={styles.filters}>
              {["all", "order_placed", "packaged", "shipped", "out_for_delivery", "delivered"].map(f => (
                <button
                  key={f}
                  style={{
                    ...styles.filterBtn,
                    ...(filter === f ? styles.filterBtnActive : {})
                  }}
                  onClick={() => setFilter(f)}
                >
                  {f === "all" ? "All" : STATUS_LABELS[f]}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          <div style={styles.tableWrap}>
            {loading && <p style={styles.empty}>Loading orders...</p>}
            {!loading && filteredOrders.length === 0 && (
              <p style={styles.empty}>No orders found.</p>
            )}
            {!loading && filteredOrders.map(order => {
              const stage = order.fulfillmentStatus || "order_placed";
              const badge = STATUS_COLORS[stage] || STATUS_COLORS.order_placed;
              const label = STATUS_LABELS[stage] || stage;
              const isSelected = selected?._id === order._id;

              return (
                <div
                  key={order._id}
                  style={{
                    ...styles.orderRow,
                    ...(isSelected ? styles.orderRowSelected : {})
                  }}
                  onClick={() => openOrder(order)}
                >
                  <div style={styles.orderMain}>
                    <p style={styles.orderNum}>{order.orderNumber}</p>
                    <p style={styles.orderCustomer}>
                      {order.customer?.firstName} {order.customer?.lastName}
                      {order.customer?.email && (
                        <span style={styles.orderEmail}> · {order.customer.email}</span>
                      )}
                    </p>
                  </div>

                  <div style={styles.orderMid}>
                    <p style={styles.orderItems}>
                      {order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? "s" : ""}
                    </p>
                    <p style={styles.orderDate}>
                      {new Date(order.createdAt).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric"
                      })}
                    </p>
                  </div>

                  <div style={styles.orderRight}>
                    <span style={{ ...styles.badge, background: badge.bg, color: badge.color }}>
                      {label}
                    </span>
                    <p style={styles.orderTotal}>${Number(order.total || 0).toFixed(2)}</p>
                  </div>
                </div>
              );
            })}
          </div>

        </div>

        {/* Order Detail Panel */}
        {selected && (
          <div style={styles.detailPanel}>

            <div style={styles.detailHeader}>
              <div>
                <p style={styles.detailOrderNum}>{selected.orderNumber}</p>
                <p style={styles.detailCustomer}>
                  {selected.customer?.firstName} {selected.customer?.lastName}
                </p>
              </div>
              <button style={styles.closeBtn} onClick={() => setSelected(null)}>✕</button>
            </div>

            {/* Items */}
            <div style={styles.detailSection}>
              <p style={styles.detailLabel}>ITEMS</p>
              {(selected.items || []).map((item, i) => (
                <div key={i} style={styles.detailItem}>
                  {item.image && (
                    <img
                      src={`${item.image}`}
                      style={styles.detailItemImg}
                      alt={item.title}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <p style={styles.detailItemTitle}>{item.title}</p>
                   <p style={styles.detailItemMeta}>
  Qty: {item.qty} · ${Number(item.price).toFixed(2)} each
</p>
{item.selectedCondition && (
  <span style={{
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 7px",
    borderRadius: 999,
    background: item.selectedCondition === "new" ? "#e0f2fe" : "#f3f4f6",
    color: item.selectedCondition === "new" ? "#0369a1" : "#6b7280",
    marginTop: 3,
    display: "inline-block"
  }}>
    {item.selectedCondition === "new" ? "Brand New" : "Used"}
  </span>
)}
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div style={styles.detailSection}>
              <p style={styles.detailLabel}>ORDER TOTALS</p>
              <div style={styles.totalsRow}><span>Subtotal</span><span>${Number(selected.subtotal || 0).toFixed(2)}</span></div>
              <div style={styles.totalsRow}><span>Tax</span><span>${Number(selected.tax || 0).toFixed(2)}</span></div>
              <div style={{ ...styles.totalsRow, fontWeight: 800, fontSize: 16 }}>
                <span>Total</span><span>${Number(selected.total || 0).toFixed(2)}</span>
              </div>
            </div>

            {/* Shipping Address */}
            {selected.shippingAddress && (
              <div style={styles.detailSection}>
                <p style={styles.detailLabel}>SHIPPING ADDRESS</p>
                <p style={styles.detailText}>{selected.customer?.firstName} {selected.customer?.lastName}</p>
                <p style={styles.detailText}>{selected.shippingAddress.address}</p>
                {selected.shippingAddress.apartment && <p style={styles.detailText}>{selected.shippingAddress.apartment}</p>}
                <p style={styles.detailText}>
                  {selected.shippingAddress.city}, {selected.shippingAddress.state} {selected.shippingAddress.zip}
                </p>
              </div>
            )}

            {/* Update Fulfillment */}
            <div style={styles.detailSection}>
              <p style={styles.detailLabel}>UPDATE FULFILLMENT</p>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Status</label>
                <select
                  style={styles.select}
                  value={updateForm.fulfillmentStatus}
                  onChange={e => setUpdateForm(p => ({ ...p, fulfillmentStatus: e.target.value }))}
                >
                  {FULFILLMENT_STAGES.map(s => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Carrier</label>
                <input
                  style={styles.input}
                  placeholder="UPS, FedEx, USPS..."
                  value={updateForm.carrier}
                  onChange={e => setUpdateForm(p => ({ ...p, carrier: e.target.value }))}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Tracking Number</label>
                <input
                  style={styles.input}
                  placeholder="Enter tracking number..."
                  value={updateForm.trackingNumber}
                  onChange={e => setUpdateForm(p => ({ ...p, trackingNumber: e.target.value }))}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Note (optional)</label>
                <input
                  style={styles.input}
                  placeholder="Internal note..."
                  value={updateForm.note}
                  onChange={e => setUpdateForm(p => ({ ...p, note: e.target.value }))}
                />
              </div>

              <button
                style={{ ...styles.updateBtn, opacity: updating ? 0.6 : 1 }}
                onClick={pushUpdate}
                disabled={updating}
              >
                {updating ? "Updating..." : "Push Update"}
              </button>

              <button
                style={styles.invoiceBtn}
                onClick={() => navigate(`/invoice/${selected._id}`)}
              >
                View Invoice →
              </button>
            </div>

            {/* Status History */}
            {selected.statusHistory?.length > 0 && (
              <div style={styles.detailSection}>
                <p style={styles.detailLabel}>STATUS HISTORY</p>
                {[...selected.statusHistory].reverse().map((h, i) => (
                  <div key={i} style={styles.historyRow}>
                    <div style={styles.historyDot} />
                    <div>
                      <p style={styles.historyStage}>{STATUS_LABELS[h.stage] || h.stage}</p>
                      <p style={styles.historyTime}>
                        {new Date(h.timestamp).toLocaleString("en-US", {
                          month: "short", day: "numeric",
                          hour: "numeric", minute: "2-digit"
                        })}
                        {h.note && ` · ${h.note}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        )}

      </div>

    </AdminLayout>
  );
}

const styles = {

  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 28
  },

  kicker: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 2,
    color: "#9ca3af",
    textTransform: "uppercase"
  },

  title: {
    margin: "6px 0 0",
    fontSize: 36,
    fontWeight: 800,
    letterSpacing: -1,
    color: "#0f0f17"
  },

  headerStats: {
    display: "flex",
    gap: 12,
    paddingTop: 8
  },

  statChip: {
    background: "#fff",
    borderRadius: 12,
    padding: "10px 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
  },

  statNum: {
    fontSize: 22,
    fontWeight: 900,
    color: "#0f0f17",
    lineHeight: 1
  },

  statLabel: {
    fontSize: 11,
    color: "#9ca3af",
    fontWeight: 600
  },

  layout: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 20,
    alignItems: "start"
  },

  tableSection: {
    background: "#fff",
    borderRadius: 18,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)"
  },

  toolbar: {
    padding: "20px 24px",
    borderBottom: "1px solid #f3f4f6",
    display: "flex",
    flexDirection: "column",
    gap: 12
  },

  search: {
    width: "100%",
    padding: "10px 14px",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box"
  },

  filters: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap"
  },

  filterBtn: {
    padding: "6px 14px",
    border: "1px solid #e5e7eb",
    borderRadius: 999,
    background: "transparent",
    fontSize: 12,
    fontWeight: 600,
    color: "#6b7280",
    cursor: "pointer"
  },

  filterBtnActive: {
    background: "#0f0f17",
    color: "#fff",
    border: "1px solid #0f0f17"
  },

  tableWrap: {
    maxHeight: "calc(100vh - 280px)",
    overflowY: "auto"
  },

  empty: {
    textAlign: "center",
    color: "#9ca3af",
    padding: "40px 0",
    fontSize: 14
  },

  orderRow: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "16px 24px",
    borderBottom: "1px solid #f9fafb",
    cursor: "pointer",
    transition: "background 0.1s ease"
  },

  orderRowSelected: {
    background: "#f5f3ff"
  },

  orderMain: {
    flex: 1
  },

  orderNum: {
    margin: 0,
    fontSize: 14,
    fontWeight: 700,
    color: "#0f0f17"
  },

  orderCustomer: {
    margin: "3px 0 0",
    fontSize: 13,
    color: "#6b7280"
  },

  orderEmail: {
    color: "#9ca3af"
  },

  orderMid: {
    textAlign: "center"
  },

  orderItems: {
    margin: 0,
    fontSize: 13,
    color: "#374151",
    fontWeight: 500
  },

  orderDate: {
    margin: "3px 0 0",
    fontSize: 12,
    color: "#9ca3af"
  },

  orderRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 4
  },

  badge: {
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700
  },

  orderTotal: {
    margin: 0,
    fontSize: 15,
    fontWeight: 800,
    color: "#0f0f17"
  },

  /* Detail Panel */
  detailPanel: {
    width: 360,
    background: "#fff",
    borderRadius: 18,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)",
    maxHeight: "calc(100vh - 160px)",
    overflowY: "auto",
    position: "sticky",
    top: 20
  },

  detailHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "20px 20px 16px",
    borderBottom: "1px solid #f3f4f6"
  },

  detailOrderNum: {
    margin: 0,
    fontSize: 15,
    fontWeight: 800,
    color: "#0f0f17"
  },

  detailCustomer: {
    margin: "4px 0 0",
    fontSize: 13,
    color: "#6b7280"
  },

  closeBtn: {
    border: "none",
    background: "#f3f4f6",
    borderRadius: "50%",
    width: 28,
    height: 28,
    cursor: "pointer",
    fontSize: 13,
    color: "#6b7280",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },

  detailSection: {
    padding: "16px 20px",
    borderBottom: "1px solid #f3f4f6"
  },

  detailLabel: {
    margin: "0 0 10px",
    fontSize: 10,
    fontWeight: 900,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: 1.5
  },

  detailItem: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    marginBottom: 10
  },

  detailItemImg: {
    width: 44,
    height: 44,
    borderRadius: 8,
    objectFit: "cover",
    background: "#f3f4f6",
    flexShrink: 0
  },

  detailItemTitle: {
    margin: 0,
    fontSize: 13,
    fontWeight: 600,
    color: "#374151"
  },

  detailItemMeta: {
    margin: "3px 0 0",
    fontSize: 12,
    color: "#9ca3af"
  },

  detailText: {
    margin: "0 0 3px",
    fontSize: 13,
    color: "#374151"
  },

  totalsRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 14,
    color: "#374151",
    marginBottom: 6
  },

  formGroup: {
    marginBottom: 12
  },

  formLabel: {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: "#6b7280",
    marginBottom: 5,
    textTransform: "uppercase",
    letterSpacing: 0.8
  },

  select: {
    width: "100%",
    padding: "9px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    fontSize: 14,
    background: "#fff",
    outline: "none",
    boxSizing: "border-box"
  },

  input: {
    width: "100%",
    padding: "9px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box"
  },

  updateBtn: {
    width: "100%",
    padding: "12px",
    border: "none",
    borderRadius: 10,
    background: "#0f0f17",
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    marginBottom: 8
  },

  invoiceBtn: {
    width: "100%",
    padding: "10px",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    background: "transparent",
    color: "#6b7280",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer"
  },

  historyRow: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    marginBottom: 10
  },

  historyDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#6366f1",
    marginTop: 4,
    flexShrink: 0
  },

  historyStage: {
    margin: 0,
    fontSize: 13,
    fontWeight: 600,
    color: "#374151"
  },

  historyTime: {
    margin: "2px 0 0",
    fontSize: 12,
    color: "#9ca3af"
  }
};